import { useMemo } from 'react';
import { useQuery } from 'react-apollo';

import { IntegrationsDiscoveryIsAdmin } from './IntegrationsDiscovery.graphql';
import { IntegrationsDiscoveryInstalledApps } from './IntegrationsDiscoveryInstalledApps.experimentalgraphql';
import type { PromotedIntegration, IntegrationToShow } from './promoted-integrations';
import { promotedIntegrations, slackIntegration } from './promoted-integrations';
import type {
	IntegrationsDiscoveryInstalledAppsVariables,
	IntegrationsDiscoveryInstalledApps as IntegrationsDiscoveryInstalledAppsType,
} from './__types__/IntegrationsDiscoveryInstalledApps';
import type { IntegrationsDiscoveryIsAdmin as IntegrationsDiscoveryIsAdminType } from './__types__/IntegrationsDiscoveryIsAdmin';

export const useIntegrationsDiscoveryQuery = (
	source: string,
	integrations: PromotedIntegration[] = promotedIntegrations,
	skip: boolean = false,
	sortFunction: (a: PromotedIntegration, b: PromotedIntegration) => number = () => 1,
) => {
	const {
		data: installedAppsData,
		loading: isInstalledAppsLoading,
		error: installedAppsError,
	} = useQuery<IntegrationsDiscoveryInstalledAppsType, IntegrationsDiscoveryInstalledAppsVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
		IntegrationsDiscoveryInstalledApps,
		{
			variables: { appKeys: integrations.map(({ key }) => key) },
			skip,
			fetchPolicy: 'no-cache',
		},
	);

	// Separate request so that this one can be fetched from cache
	const { data: adminData, loading: isAdminLoading } = useQuery<IntegrationsDiscoveryIsAdminType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		IntegrationsDiscoveryIsAdmin,
	);
	const isAdmin = adminData?.isSiteAdmin || false;

	const isSlackEnabled =
		installedAppsData?.experimentalIntegrationsInstalled?.isSlackEnabled ?? true;

	const isSlackConnected =
		installedAppsData?.experimentalIntegrationsInstalled?.isSlackConnected ?? false;

	const integrationsToShow = useMemo(() => {
		const installedIntegrations = new Set(
			(installedAppsData?.experimentalIntegrationsInstalled?.integrations || [])
				.filter((app) => app.isInstalled)
				.map((app) => app.appKey),
		);
		return integrations
			.filter(({ key }) =>
				key === slackIntegration.key
					? isSlackEnabled && !isSlackConnected
					: !installedIntegrations.has(key),
			)
			.sort(sortFunction)
			.slice(0, 3)
			.map(
				(integration): IntegrationToShow => ({
					...integration,
					url: integration.getUrl(source, isAdmin),
					marketplaceUrl: integration.getMarketplaceUrl(source, isAdmin),
				}),
			);
	}, [
		isSlackEnabled,
		isSlackConnected,
		source,
		integrations,
		installedAppsData,
		isAdmin,
		sortFunction,
	]);

	return {
		isLoading: isInstalledAppsLoading || isAdminLoading,
		error: installedAppsError,
		integrationsToShow,
		isSlackEnabled,
		isSlackConnected,
	};
};
